import moment from "moment";
import React, { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as WellBeingIcon } from "assets/images/criticalIncident/ic_wellbeing_assessment.svg";
import { CIPriorityLevel, type CIReferral } from "components/CriticalIncident/interfaces";
import { formatAddress } from "components/CriticalIncident/utils/formatters";
import { LabelValue } from "components/Shared/LabelValue/LabelValue";
import { type Priority, PriorityAlert } from "components/Shared/PriorityAlert/PriorityAlert";

import { DateRangeWidget } from "../DateRangeWidget";
import { IconPane } from "components/Shared/IconPane";
import { TimezoneDiffAlert } from "../TimezoneDiffAlert";

import "./CIHighLevelDetails.scss";

const PRIORITIES: Record<CIPriorityLevel, Priority> = {
    [CIPriorityLevel.normal]: 'normal',
    [CIPriorityLevel.high]: 'high',
    [CIPriorityLevel.critical]: 'critical'
}

export const CIHighLevelDetails: FC<{ referral: CIReferral }> = ({ referral }) => {
    const { t } = useTranslation('criticalIncident', { useSuspense: false });

    const totalTimeStr = useMemo(() => {
        const totalTime = moment.duration(
            moment(referral.bookingEndTime).diff(moment(referral.bookingStartTime), 'milliseconds')
        );
        return `
            ${totalTime.days() > 0 ? `${totalTime.days()} ${totalTime.days() === 1 ? t('day') : t('days')}, ` : ''}
            ${totalTime.hours() > 0 ? `${totalTime.hours()} ${totalTime.hours() === 1 ? t('hour') : t('hours')}, ` : ''}
            ${totalTime.minutes()} ${totalTime.minutes() === 1 ? t('minute') : t('minutes')}
        `;
    }, [referral.bookingEndTime, referral.bookingStartTime]);

    return (
        <div className="ci-high-level-details">
            <div className="ci-high-level-details-alerts">
                <PriorityAlert
                    title={t(`${PRIORITIES[referral?.priority]}_priority`)}
                    priority={PRIORITIES[referral?.priority] ?? 'normal'}
                    content={null}
                />
                <TimezoneDiffAlert clientTimezone={referral.timezone} />
            </div>
            <section className="ci-high-level-details-section">
                <div className="heading-with-icon">
                    <IconPane iconName="BusinessRounded" />
                    <h3>{t('account')}</h3>
                </div>
                <div className="body-with-label-value-pairs">
                    <LabelValue label={t('client')} value={`${[referral?.companyName, referral?.branchName, referral?.divisionName].filter(Boolean).join(', ')}`} />
                    <LabelValue label={t('industry')} value={referral?.industry} />
                    <LabelValue label={t('work_order_id')} value={referral?.externalWorkOrderId} />
                </div>
            </section>
            <section className="ci-high-level-details-section">
                <div className="heading-with-icon">
                    <IconPane iconName="LocalHospital" />
                    <h3>{t('incident')}</h3>
                </div>
                <div className="body-with-label-value-pairs">
                    <LabelValue label={t('service')} value={referral?.incidentType} />
                </div>
            </section>
            <section className="ci-high-level-details-section">
                <div className="heading-with-icon">
                    <IconPane icon={<WellBeingIcon />} />
                    <h3>{t('intervention')}</h3>
                </div>
                <div className="body-with-label-value-pairs">
                    <DateRangeWidget startDate={referral.bookingStartTime} endDate={referral.bookingEndTime} clientTimezone={referral.timezone} />
                    <LabelValue label={t('duration')} value={totalTimeStr} />
                    <LabelValue label={t('location')} value={formatAddress(referral.location, { cityAndStateOnly: true, t })} />
                    <LabelValue label={t('modality')} value={t(referral.modality)} />
                </div>
            </section>
        </div>
    )
}